import React, { useEffect } from 'react';

import ReactCountryFlag from 'react-country-flag';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import { Button } from '@/components/buttons/Button';
import {
  useRegistrationActions,
  useWorkInfoData,
  WorkRegistrationInfo,
} from '@/store/zustand/registration-store';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import SearchDropdownField from '@/components/form/search-dropdown/SearchDropdownField';
import Form from '@/components/form/Form';
import { useBffRequest } from '@/hooks/useBffRequest';
import BffApiService from '@/shared/bffApiService';

const INDUSTRY_OCCUPATION_MAP = [
  // Private households - Retired
  {
    industryId: '208',
    occupationId: '5',
  },
  // Universities - Student
  {
    industryId: '58',
    occupationId: '4',
  },
  // Employment services - Unemployed
  {
    industryId: '9',
    occupationId: '7',
  },
];

const WorkInfoForm = ({
  stepBack,
  stepForward,
}: {
  stepBack: () => void;
  stepForward: () => void;
}) => {
  const { request: fetchIndustriesOccupationsRequest, data: fetchIndustriesAndOccupationsData } =
    useBffRequest(BffApiService.fetchIndustriesAndOccupations);
  const {
    request: fetchCountriesRequest,
    data: fetchCountriesData,
    error: fetchCountriesError,
  } = useBffRequest(BffApiService.getCountries);
  const { citizenshipId, industryId, occupationId } = useWorkInfoData();
  const { updateFields } = useRegistrationActions();
  const intl = useIntl();

  const initialValues: WorkRegistrationInfo = {
    citizenshipId,
    industryId,
    occupationId,
  };

  const validationSchema = Yup.object().shape({
    citizenshipId: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    industryId: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    occupationId: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
  });

  const mappedCountries = fetchCountriesData
    ? Object.entries(fetchCountriesData).map(([alpha2Code, { name }]) => ({
        value: alpha2Code,
        label: name,
        endIcon: <ReactCountryFlag countryCode={alpha2Code} />,
      }))
    : [];

  //required because some industries have the same ID
  const industryValueSeparator = '⸬';
  const mappedIndustries =
    fetchIndustriesAndOccupationsData?.industries.map(industry => ({
      value: industry.name + industryValueSeparator + industry.id.toString(),
      label: industry.name,
    })) ?? [];

  const mappedOccupations =
    fetchIndustriesAndOccupationsData?.occupations.map(occupation => ({
      value: occupation.id.toString(),
      label: occupation.name,
    })) ?? [];

  // This is a dirty hack to display the SearchDropdownField. It is required
  // because the displayFunction on Combobox.Input does not get re-invoked
  // on component re-rerender for some reason, which happens when options prop change,
  // thus the initial citizenship value will never be displayed, because on initial
  // mount options array was empty and the displayFunction is not re-invoked
  const isCitizenshipShown = mappedCountries.length > 0 || fetchCountriesError;

  const handleOnSubmit = (values: WorkRegistrationInfo) => {
    triggerGTag(GTagEvents.ont_reg_step3_continue_cta_click);
    updateFields({
      ...values,
      industryId: values.industryId.split(industryValueSeparator)[1],
    });
    stepForward();
  };

  useEffect(() => {
    fetchIndustriesOccupationsRequest({
      axiosConfig: { params: { regulator: 'agco' } },
    });
    fetchCountriesRequest({});
  }, []);

  return (
    <div className="w-full h-full pt-5">
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
        className="flex flex-col justify-between gap-8 h-full"
      >
        {({ setFieldValue }) => (
          <>
            <div className="flex flex-col gap-7">
              {isCitizenshipShown && (
                <SearchDropdownField
                  name="citizenshipId"
                  label={intl.formatMessage({ id: 'inputs.citizenship' })}
                  options={mappedCountries}
                  onValidationError={error =>
                    triggerGTag(GTagEvents.ont_reg_citizenship_error, { error })
                  }
                  onFocus={() => triggerGTag(GTagEvents.ont_reg_citizenship_click)}
                  inputClassName="!bg-transparent border-[1px] border-white/30 text-white focus-within:border-blue-light"
                  labelClassName="text-white"
                />
              )}
              <SearchDropdownField
                name="industryId"
                onChange={newValue => {
                  const mappedOccupation = INDUSTRY_OCCUPATION_MAP.find(
                    item => item.industryId === newValue.split(industryValueSeparator)[1],
                  );
                  if (mappedOccupation) {
                    setFieldValue('occupationId', mappedOccupation.occupationId);
                  }
                  setFieldValue('industryId', newValue);
                }}
                label={intl.formatMessage({ id: 'inputs.industry' })}
                options={mappedIndustries}
                onValidationError={error =>
                  triggerGTag(GTagEvents.ont_reg_industry_error, { error })
                }
                onFocus={() => triggerGTag(GTagEvents.ont_reg_industry_click)}
                inputClassName="!bg-transparent border-[1px] border-white/30 text-white focus-within:border-blue-light"
                labelClassName="text-white"
              />
              <SearchDropdownField
                name="occupationId"
                label={intl.formatMessage({ id: 'inputs.occupation' })}
                options={mappedOccupations}
                onValidationError={error =>
                  triggerGTag(GTagEvents.ont_reg_occupation_error, { error })
                }
                onFocus={() => triggerGTag(GTagEvents.ont_reg_occupation_click)}
                inputClassName="!bg-transparent border-[1px] border-white/30 text-white focus-within:border-blue-light"
                labelClassName="text-white"
              />
            </div>
            <div className="flex flex-col-reverse items-center gap-4 mt-4 sm:flex-row">
              <Button
                text={intl.formatMessage({ id: 'misc.back' })}
                onClickHandler={() => {
                  triggerGTag(GTagEvents.ont_reg_step3_back_cta_click);
                  stepBack();
                }}
                btnType="secondary"
              />
              <Button
                text={intl.formatMessage({ id: 'misc.continue' })}
                type="submit"
                btnType="primary"
              />
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default WorkInfoForm;
